import React, { useState, useEffect } from 'react';
import { Search, FileText, Plus, X, Loader2, Edit3, Trash2, Download, CheckCircle2, Paperclip, ExternalLink, ListChecks } from 'lucide-react';

export default function ProspectusManagement() {
  const [dataList, setDataList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Pagination Parameters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Modal Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    session_subtitle: '',
    academic_year: 'AY 2026–27',
    pdf_file_url: '',
    file_name: '',
    file_size_text: '',
    contents_checklist: [
      'University Overview & History',
      'CDOE Introduction',
      'Programs Offered (UG / PG / Diploma)',
      'Eligibility Criteria',
      'Fee Structure 2026–27',
      'Important Dates',
      'Admission Procedure',
      'Study Centre Directory',
      'Rules & Regulations',
      'Examination Guidelines',
      'Contact Directory'
    ]
  });

  const [newTopicInput, setNewTopicInput] = useState('');

  useEffect(() => {
    fetchProspectus();
  }, [searchTerm]);

  const fetchProspectus = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/prospectus?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) setDataList(result.data || []);
      else setDataList([]);
    } catch (err) {
      console.error("Failed to load prospectus:", err);
      setDataList([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setUploadFile(null);
    setFormData({
      title: 'CDOE Prospectus AY 2026–27',
      session_subtitle: 'MET ACADEMY | July Session',
      academic_year: 'AY 2026–27',
      pdf_file_url: '',
      file_name: '',
      file_size_text: '',
      contents_checklist: [
        'University Overview & History',
        'CDOE Introduction',
        'Programs Offered (UG / PG / Diploma)',
        'Eligibility Criteria',
        'Fee Structure 2026–27',
        'Important Dates',
        'Admission Procedure',
        'Study Centre Directory',
        'Rules & Regulations',
        'Examination Guidelines',
        'Contact Directory'
      ]
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setUploadFile(null);
    setFormData({
      title: item.title || '',
      session_subtitle: item.session_subtitle || '',
      academic_year: item.academic_year || 'AY 2026–27',
      pdf_file_url: item.pdf_file_url || '',
      file_name: item.file_name || '',
      file_size_text: item.file_size_text || '',
      contents_checklist: Array.isArray(item.contents_checklist) ? item.contents_checklist : []
    });
    setIsModalOpen(true);
  };

  // Content Checklist Dynamic Helpers
  const handleAddTopic = () => {
    if (!newTopicInput.trim()) return;
    setFormData(prev => ({
      ...prev,
      contents_checklist: [...prev.contents_checklist, newTopicInput.trim()]
    }));
    setNewTopicInput('');
  };

  const handleRemoveTopic = (index) => {
    setFormData(prev => ({
      ...prev,
      contents_checklist: prev.contents_checklist.filter((_, idx) => idx !== index)
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const payloadFormData = new FormData();
    payloadFormData.append('payload', JSON.stringify(formData));
    if (uploadFile) {
      payloadFormData.append('pdf_file', uploadFile);
    }

    const endpoint = editingId ? `update-entry/${editingId}` : 'add-entry';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/prospectus/${endpoint}`, {
        method: method,
        body: payloadFormData
      });
      const result = await res.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchProspectus();
      } else {
        alert(result.message || "Operation failed.");
      }
    } catch (err) {
      console.error("Submission failed:", err);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this prospectus?")) return;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/prospectus/delete-entry/${id}`, {
        method: 'PUT'
      });
      const result = await res.json();
      if (result.success) fetchProspectus();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  // Pagination Window Range Computation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataList.length / itemsPerPage);

  return (
    <div style={styles.container}>
      
      {/* TOP ACTION HEADER */}
      <div style={styles.topActionBar}>
        <div>
          <h2 style={styles.mainTitle}>Admission Prospectus & Handbooks</h2>
          <p style={styles.mainSubtitle}>Upload official university prospectus brochures, brochures index, and guidelines</p>
        </div>
        <button onClick={handleOpenAddModal} style={styles.addBtn}>
          <Plus size={15} style={{ marginRight: '6px' }} /> Upload New Prospectus
        </button>
      </div>

      {/* FILTER PANEL */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by title, session, or academic year..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>Active Prospectus Entries: <strong style={{ color: '#1c6ec9' }}>{dataList.length}</strong></div>
      </div>

      {/* MASTER DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={styles.tableTh}>Prospectus Document</th>
                <th style={styles.tableTh}>Academic Session</th>
                <th style={styles.tableTh}>Contents Included</th>
                <th style={styles.tableTh}>File Details</th>
                <th style={{ ...styles.tableTh, width: '110px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataList.length === 0 ? (
                <tr><td colSpan="5" style={styles.noDataTd}>No prospectus uploaded yet.</td></tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} style={styles.tableTr}>
                    <td style={styles.tableTd}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={styles.pdfIconBox}>
                          <FileText size={16} color="#e30504" />
                        </div>
                        <div>
                          <strong style={{ color: '#0f172a', fontSize: '13.5px' }}>{item.title}</strong>
                          <span style={{ fontSize: '11.5px', color: '#64748b', display: 'block' }}>{item.session_subtitle || item.academic_year}</span>
                        </div>
                      </div>
                    </td>
                    <td style={styles.tableTd}>
                      <span style={styles.academicYearBadge}>{item.academic_year}</span>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.contentsChipGroup}>
                        {Array.isArray(item.contents_checklist) && item.contents_checklist.slice(0, 3).map((topic, i) => (
                          <span key={i} style={styles.topicMiniPill}>✓ {topic}</span>
                        ))}
                        {item.contents_checklist?.length > 3 && (
                          <span style={styles.morePill}>+{item.contents_checklist.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td style={styles.tableTd}>
                      <a href={item.pdf_file_url} target="_blank" rel="noreferrer" style={styles.downloadAnchor}>
                        <Download size={12} style={{ marginRight: '4px' }} /> Download ({item.file_size_text || 'PDF'})
                      </a>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.actionBtnGroup}>
                        <button onClick={() => handleOpenEditModal(item)} style={styles.editRowBtn} title="Edit"><Edit3 size={12} /></button>
                        <button onClick={() => handleDeleteEntry(item.id)} style={styles.deleteRowBtn} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION TOOLBAR */}
      {dataList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* PROSPECTUS CREATION & EDIT MODAL */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={16} color="#1c6ec9" />
                {editingId ? 'Modify Prospectus Details' : 'Upload Academic Prospectus'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>

            <form onSubmit={handleFormSubmit} style={styles.modalForm}>
              
              {/* 1. Title & Academic Year */}
              <div style={styles.formSplitGrid}>
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Prospectus Title *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. CDOE Prospectus AY 2026–27"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Academic Year *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. AY 2026–27"
                    value={formData.academic_year}
                    onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
              </div>

              {/* 2. Session Subtitle */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Institution & Session Subtitle</label>
                <input
                  type="text"
                  placeholder="e.g. EDULEARNER | July Session"
                  value={formData.session_subtitle}
                  onChange={(e) => setFormData({ ...formData, session_subtitle: e.target.value })}
                  style={styles.formInput}
                />
              </div>

              {/* 3. PDF File Upload */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Upload Prospectus PDF Document *</label>
                <input
                  type="file"
                  accept=".pdf"
                  required={!editingId && !formData.pdf_file_url}
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  style={styles.formFileInput}
                />
                {formData.pdf_file_url ? (
                  <span style={{ fontSize: '11.5px', color: '#16a34a' }}>
                    Current File: {formData.file_name || 'Prospectus.pdf'} ({formData.file_size_text || 'Active'})
                  </span>
                ) : null}
              </div>

              {/* 4. Prospectus Contents Checklist Builder */}
              <div style={styles.checklistSection}>
                <label style={{ ...styles.fieldLabel, color: '#1c6ec9', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ListChecks size={13} /> Prospectus Contents Checklist ({formData.contents_checklist.length} Topics)
                </label>

                {/* Input with Add button */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Add topic (e.g. Fee Structure, Important Dates)..."
                    value={newTopicInput}
                    onChange={(e) => setNewTopicInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTopic(); } }}
                    style={{ ...styles.formInput, flex: 1 }}
                  />
                  <button type="button" onClick={handleAddTopic} style={styles.addTopicBtn}>
                    <Plus size={13} /> Add
                  </button>
                </div>

                {/* Topics Pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '120px', overflowY: 'auto' }}>
                  {formData.contents_checklist.map((topic, idx) => (
                    <span key={idx} style={styles.topicChip}>
                      ✓ {topic}
                      <button type="button" onClick={() => handleRemoveTopic(idx)} style={styles.removeTopicBtn}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div style={styles.formActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                <button type="submit" style={styles.formSaveBtn}>
                  {editingId ? 'Save Changes' : 'Publish Prospectus'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// FORMATTING STYLES OBJECT MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  topActionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  mainTitle: { margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' },
  mainSubtitle: { margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' },
  addBtn: { background: 'linear-gradient(90deg, #e30504 0%, #a80302 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  filterControlPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #edf2f7', paddingBottom: '14px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '380px', width: '100%' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '9px 12px 9px 36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', color: '#0f172a', fontSize: '13.5px', outline: 'none' },
  totalIndicator: { fontSize: '14.5px', fontWeight: '600', color: '#334155' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  tableTh: { padding: '12px 14px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', whiteSpace: 'nowrap' },
  tableTr: { borderBottom: '1px solid #f1f5f9', background: '#ffffff' },
  tableTd: { padding: '14px 14px', color: '#475569', fontSize: '13.5px', verticalAlign: 'middle' },
  noDataTd: { padding: '36px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  pdfIconBox: { width: '32px', height: '32px', borderRadius: '8px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  academicYearBadge: { background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#334155', padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '700' },
  contentsChipGroup: { display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '320px' },
  topicMiniPill: { background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', fontSize: '11px', fontWeight: '600', padding: '2px 6px', borderRadius: '4px' },
  morePill: { fontSize: '11px', color: '#64748b', fontWeight: '600', alignSelf: 'center' },
  downloadAnchor: { display: 'inline-flex', alignItems: 'center', background: '#0f172a', color: '#ffffff', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '700', textDecoration: 'none' },
  actionBtnGroup: { display: 'flex', gap: '6px' },
  editRowBtn: { background: '#ffffff', border: '1px solid #cbd5e1', color: '#475569', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
  deleteRowBtn: { background: '#ffffff', border: '1px solid #fecaca', color: '#b91c1c', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.25)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '20px', width: '100%', maxWidth: '620px', maxHeight: '88vh', padding: '24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '14px', overflowY: 'auto' },
  formSplitGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formInput: { width: '100%', padding: '9px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  formFileInput: { fontSize: '12px', color: '#475569', padding: '6px 0' },
  checklistSection: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' },
  addTopicBtn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', padding: '0 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  topicChip: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '3px 8px', borderRadius: '14px', fontSize: '11.5px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  removeTopicBtn: { background: 'transparent', border: 'none', color: '#1e40af', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 },
  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer' },
  paginationDock: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edf2f7' },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 14px', color: '#334155', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' },
  formSaveBtn: { background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }
};